// Admin analytics dashboard

import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';

interface Analytics {
  monthly_revenue: { month: string; count: number; revenue: number }[];
  type_popularity: { vehicle_type: string; count: number; revenue: number }[];
  top_vehicles: { make: string; model: string; year: number; rental_count: number; revenue: number }[];
  top_guests: { name: string; business_name?: string; rental_count: number; revenue: number }[];
  vehicle_stats: { total: number; approved: number; pending: number; inactive: number };
  current_rentals: number;
  this_month: { total: number; settled: number; revenue: number };
}

const TYPE_LABELS: Record<string, string> = {
  sports: '스포츠카', suv: 'SUV', sedan: '세단', truck: '트럭', van: '밴',
  classic: '클래식카', special: '특수차량',
};

const TYPE_COLORS: Record<string, string> = {
  sports: '#6366f1', suv: '#10b981', sedan: '#3b82f6', truck: '#f59e0b', van: '#8b5cf6',
  classic: '#ec4899', special: '#6b7280',
};

export default function AdminAnalyticsPage() {
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.analytics()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }
  if (!data) return <div className="text-center py-20 text-gray-400">데이터를 불러올 수 없습니다</div>;

  const formatPrice = (won: number) => {
    if (!won) return '0원';
    if (won >= 100000000) return (won / 100000000).toFixed(1) + '억원';
    if (won >= 10000) return Math.round(won / 10000) + '만원';
    return won.toLocaleString() + '원';
  };

  const maxRevenue = Math.max(1, ...data.monthly_revenue.map(m => m.revenue));

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2">분석 대시보드</h1>
      <p className="text-sm text-gray-500 mb-6">차량, 예약, 매출 데이터 분석</p>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: '전체 차량', value: `${data.vehicle_stats?.total || 0}대`, sub: `승인 ${data.vehicle_stats?.approved || 0}대` },
          { label: '이번 달 예약', value: `${data.this_month?.total || 0}건`, sub: `정산 ${data.this_month?.settled || 0}건` },
          { label: '이번 달 매출', value: formatPrice(data.this_month?.revenue || 0), sub: '' },
          { label: '현재 대여 중', value: `${data.current_rentals || 0}대`, sub: '' },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm p-4">
            <p className="text-xs text-gray-400 mb-1">{k.label}</p>
            <p className="text-xl font-bold text-slate-900">{k.value}</p>
            {k.sub && <p className="text-xs text-gray-400 mt-0.5">{k.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Monthly revenue bar chart */}
        <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm p-5">
          <h3 className="font-semibold text-sm text-slate-900 mb-4">월별 매출</h3>
          <div className="space-y-2">
            {data.monthly_revenue.slice(0, 12).reverse().map(m => (
              <div key={m.month} className="flex items-center gap-2 text-xs">
                <span className="w-16 text-gray-500 shrink-0">{m.month}</span>
                <div className="flex-1 h-5 bg-gray-50 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-500 rounded-full transition-all"
                    style={{ width: `${Math.max(2, (m.revenue / maxRevenue) * 100)}%` }}
                  />
                </div>
                <span className="w-20 text-right text-gray-600 font-medium">{formatPrice(m.revenue)}</span>
                <span className="w-8 text-right text-gray-400">{m.count}건</span>
              </div>
            ))}
            {data.monthly_revenue.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-8">아직 정산 데이터가 없습니다</p>
            )}
          </div>
        </div>

        {/* Vehicle type popularity */}
        <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm p-5">
          <h3 className="font-semibold text-sm text-slate-900 mb-4">차종별 인기 순위</h3>
          {data.type_popularity.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">데이터 없음</p>
          ) : (
            <div className="space-y-2">
              {data.type_popularity.map(t => {
                const pct = Math.round((t.count / data.type_popularity.reduce((s: number, x: any) => s + x.count, 0)) * 100);
                return (
                  <div key={t.vehicle_type} className="flex items-center gap-2 text-xs">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: TYPE_COLORS[t.vehicle_type] || '#6b7280' }} />
                    <span className="w-20 text-gray-600">{TYPE_LABELS[t.vehicle_type] || t.vehicle_type}</span>
                    <div className="flex-1 h-4 bg-gray-50 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{
                        width: `${pct}%`,
                        backgroundColor: TYPE_COLORS[t.vehicle_type] || '#6b7280',
                      }} />
                    </div>
                    <span className="w-10 text-right font-medium text-gray-600">{t.count}건</span>
                    <span className="w-6 text-right text-gray-400">{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top vehicles */}
        <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm p-5">
          <h3 className="font-semibold text-sm text-slate-900 mb-4">🏆 인기 차량 TOP 10</h3>
          {data.top_vehicles.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">아직 대여 데이터가 없습니다</p>
          ) : (
            <div className="space-y-1.5">
              {data.top_vehicles.map((v, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">
                      {i + 1}
                    </span>
                    <span className="font-medium text-slate-700">{v.make} {v.model}</span>
                    <span className="text-gray-400">({v.year})</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">{v.rental_count}회</span>
                    <span className="font-medium text-slate-600 w-20 text-right">{formatPrice(v.revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top guests */}
        <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm p-5">
          <h3 className="font-semibold text-sm text-slate-900 mb-4">👥 단골 고객 TOP 10</h3>
          {data.top_guests.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">아직 고객 데이터가 없습니다</p>
          ) : (
            <div className="space-y-1.5">
              {data.top_guests.map((g, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50 last:border-0">
                  <div>
                    <span className="font-medium text-slate-700">{g.name}</span>
                    {g.business_name && <span className="text-gray-400 ml-1">· {g.business_name}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">{g.rental_count}회</span>
                    <span className="font-medium text-slate-600 w-20 text-right">{formatPrice(g.revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
